import { useCallback } from "react";
import { message } from "antd";
import {
  useSendOrderInvoiceWhatsAppMutation,
  useSendWhatsAppTextMessageMutation,
  useSendSMSMutation,
} from "../../Services";

export interface ShareDocumentParams {
  phone: string;
  documentId?: string | number;
  documentNo?: string;
  documentLabel?: string;
  partyType?: "Customer" | "Supplier" | string;
  linkPath?: string;
  getPdfBase64?: () => Promise<string | null>;
}

export const useInvoiceSharing = () => {
  const sendInvoiceWhatsAppMutation = useSendOrderInvoiceWhatsAppMutation();
  const sendWhatsAppTextMutation = useSendWhatsAppTextMessageMutation();
  const sendSmsMutation = useSendSMSMutation();

  const generateLink = useCallback((linkPath: string) => {
    const tenantId = localStorage.getItem("tenantId") || "";
    const cleanPath = linkPath.startsWith("/") ? linkPath : `/${linkPath}`;
    return `${window.location.origin}/${tenantId}${cleanPath}`;
  }, []);

  const handleSendWhatsAppPdf = useCallback(
    async ({
      phone,
      documentId,
      documentNo = "document",
      documentLabel = "Invoice",
      getPdfBase64,
    }: ShareDocumentParams) => {
      const cleanPhone = (phone || "").trim();
      if (!cleanPhone) {
        message.warning("Enter the WhatsApp phone number to send the document to.");
        return;
      }
      if (!getPdfBase64) {
        message.error("PDF generator is not available.");
        return;
      }

      try {
        const pdfBase64 = await getPdfBase64();
        if (!pdfBase64) {
          message.error("Failed to generate the invoice PDF.");
          return;
        }

        const safeFilename = `${documentLabel.replace(/\s+/g, "_")}_${documentNo}.pdf`;

        const res = await sendInvoiceWhatsAppMutation.mutateAsync({
          order_id: String(documentId),
          phone: cleanPhone,
          pdf_base64: pdfBase64,
          filename: safeFilename,
        });

        if (res?.success) {
          message.success(`${documentLabel} sent via WhatsApp!`);
        } else {
          message.error(res?.message || `Failed to send the ${documentLabel.toLowerCase()}.`);
        }
      } catch (error: any) {
        message.error(error?.message || `Failed to send the ${documentLabel.toLowerCase()}.`);
      }
    },
    [sendInvoiceWhatsAppMutation]
  );

  const handleSendWhatsAppLink = useCallback(
    async ({
      phone,
      documentNo = "",
      documentLabel = "Invoice",
      partyType = "Customer",
      linkPath = "",
    }: ShareDocumentParams) => {
      const cleanPhone = (phone || "").trim();
      if (!cleanPhone) {
        message.warning("Enter the phone number to send the invoice link.");
        return;
      }

      try {
        const link = generateLink(linkPath);
        const textMessage =
          partyType === "Supplier"
            ? `Dear Supplier, you can view/download purchase invoice #${documentNo} here: ${link}`
            : `Dear Customer, thank you for your business. You can view/download your ${documentLabel.toLowerCase()} #${documentNo} here: ${link}`;

        const res = await sendWhatsAppTextMutation.mutateAsync({
          phone: cleanPhone,
          message: textMessage,
        });

        if (res?.success) {
          message.success(`${documentLabel} link sent via WhatsApp!`);
        } else {
          message.error(
            res?.message || `Failed to send the ${documentLabel.toLowerCase()} link.`
          );
        }
      } catch (error: any) {
        message.error(
          error?.message || `Failed to send the ${documentLabel.toLowerCase()} link.`
        );
      }
    },
    [generateLink, sendWhatsAppTextMutation]
  );

  const handleSendSMSLink = useCallback(
    async ({
      phone,
      documentNo = "",
      documentLabel = "Invoice",
      partyType = "Customer",
      linkPath = "",
    }: ShareDocumentParams) => {
      const cleanPhone = (phone || "").trim();
      if (!cleanPhone) {
        message.warning("Enter the phone number to send the invoice link.");
        return;
      }

      try {
        const link = generateLink(linkPath);
        const textMessage =
          partyType === "Supplier"
            ? `Dear Supplier, you can view/download purchase invoice #${documentNo} here: ${link}`
            : `Dear Customer, thank you for your business. You can view/download your ${documentLabel.toLowerCase()} #${documentNo} here: ${link}`;

        const res = await sendSmsMutation.mutateAsync({
          contact: cleanPhone,
          message: textMessage,
        });

        if (res?.success) {
          message.success(`${documentLabel} link sent via SMS!`);
        } else {
          message.error(
            res?.message || `Failed to send the ${documentLabel.toLowerCase()} link.`
          );
        }
      } catch (error: any) {
        message.error(
          error?.message || `Failed to send the ${documentLabel.toLowerCase()} link.`
        );
      }
    },
    [generateLink, sendSmsMutation]
  );

  return {
    handleSendWhatsAppPdf,
    handleSendWhatsAppLink,
    handleSendSMSLink,
    isSendWhatsAppPending: sendInvoiceWhatsAppMutation.isPending,
    isSendWhatsAppLinkPending: sendWhatsAppTextMutation.isPending,
    isSendSmsLinkPending: sendSmsMutation.isPending,
  };
};
